import _ from 'lodash'
import {select as d3Select} from 'd3-selection'
import 'd3-selection-multi'

const graphUtil = {
  clusterByTimestamp(items, timeScale, collisionDistancePx, timeProp) {
    // extrapolate px collistion distance to a properly mapped time range on the current time scale
    const clusterThreshold = timeScale.invert(collisionDistancePx) - timeScale.invert(0)
    const sortedItems = _.sortBy(items, timeProp) // ensure items are properly sorted before clustering
    const clustered = []
    while (sortedItems.length > 0) {
      const itmCluster = sortedItems.shift()
      const cluster = []
      cluster.push(itmCluster)
      let i = 0
      while (i < sortedItems.length) {
        if (Math.abs(sortedItems[i][timeProp] - itmCluster[timeProp]) < (clusterThreshold)) {
          // Too close, cluster this item
          const aStop = sortedItems.splice(i, 1)
          cluster.push(aStop[0])
          i--
        }
        i++
      }
      clustered.push(cluster)
    }
    return clustered
  },

  createBasicGraphOptions: (() => {
    const defaultPadding = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    }
    const defaultOptions = {
      h: 500,
      w: 1000,
      padding: defaultPadding,
      className: ""
    }
    const createPadding = (allPadding, padding) => {
      if (allPadding && allPadding != null) {
        return {
          top: allPadding,
          right: allPadding,
          bottom: allPadding,
          left: allPadding
        }
      } else {
        return _.defaults(padding, defaultPadding)
      }
    }
    return function(options={}) {
      const { h, w, allPadding, padding, className } = _.defaults(options, defaultOptions)
      return {
        h: h,
        w: w,
        className: className,
        padding: createPadding(allPadding, padding)
      }
    }
  })(),

  createGraphTranslate(padding, h, w, centerGraph) {
    const centerXPadding = centerGraph ? w / 2 : 0
    const centerYPadding = centerGraph ? h / 2 : 0
    return `translate(${ padding.left + centerXPadding }, ${ padding.top + centerYPadding })`
  },

  drawSvg(el, opts) {
    let {className} = opts
    const {w, h, padding, radiusCenter, preserveAspectRatio, centerGraph} = opts
    if (!className) { className = '' }
    const viewBoxOrigin = radiusCenter && radiusCenter != null ? -radiusCenter : 0
    const svgContainer = d3Select(el).append('svg').attrs({
      class: `svg_container ${ className }`,
      height: '100%',
      width: '100%',
      viewBox: `${ viewBoxOrigin } ${ viewBoxOrigin } ${ w + padding.left + padding.right } ${ h + padding.top + padding.bottom }`,
      preserveAspectRatio: preserveAspectRatio || "xMidYMid"  //"none"
    })
    return svgContainer.append('g').attrs({
      transform: graphUtil.createGraphTranslate(padding, h, w, centerGraph)
    })
  },

  drawSimpleSvg(el, {w, h, translateX, translateY, className }) {
    const svgContainer = d3Select(el).append('svg').attrs({
      class: `svg_container ${ className || '' }`,
      height: '100%',
      width: '100%',
      viewBox: `${ -(w / 2) } ${ -(h / 2) } ${ w } ${ h }`,
      preserveAspectRatio: "xMidYMid"
    })
    return svgContainer.append('g').attrs({
      transform: `translate(${ translateX },${ translateY })`
    })
  },

  // createBargraphXScale(minTime, maxTime, dataLength, w, barWidth) ->
  //   barSize = w / dataLength
  //   barPadding = barSize * (1 - barWidth)
  //   d3.time.scale()
  //     .domain([minTime, maxTime])
  //     .range([2, w - barSize])

  // createCounterAxisXScale: (minTime, maxTime, w) ->
  //   d3.time.scale()
  //     .domain([minTime, maxTime])
  //     .range([0, w])

  // createCounterYScale: (maxValue, h) ->
  //   d3.scale.linear()
  //     .domain([0, maxValue])
  //     .range([1, h])

  // createCounterAxisYScale: (maxValue, h) ->
  //   d3.scale.linear()
  //     .domain([0, maxValue])
  //     .range([h, 0])
  //
  // createCounterFunctions: (graphOptions, graphInfo) ->
  //   xScale: graphUtil.createBargraphXScale graphInfo.startTime, graphInfo.endTime,
  //     graphInfo.history.length, graphOptions.w, graphOptions.barWidth
  //   yScale: graphUtil.createCounterYScale graphInfo.maxValue, graphOptions.h

  // createCounterFunctions: (graphOptions, graphInfo) ->
  //   axisYScale: graphUtil.createCounterAxisYScale graphInfo.maxValue, graphOptions.h
  //   axisXScale: graphUtil.createCounterAxisXScale graphInfo.startTime,
  //     graphInfo.endTime, graphOptions.w

  // createCounterFunctions: (key) -> (obj) -> obj[key] or 0

  // createCounterFunctions: (keyProp, valProp) ->
  //   key: graphUtil.keyValueFunction keyProp
  //   val: graphUtil.keyValueFunction valProp
  //
  // findDataAtTimestamp: (interval, data, timestamp, keyProp='timestamp') ->
  //   startTime = data[0][keyProp]
  //   index = (timestamp - startTime) / interval
  //   data[index]

  // Translated (heh) from.... http://bl.ocks.org/mbostock/1705868
  translateAlongPath(path) {
    const l = path.getTotalLength()
    return function() {
      return function(t) {
        const p = path.getPointAtLength(t * l)
        return "translate(" + p.x + "," + p.y + ")"
      }
    }
  },

  // For a list of coordinates, generate a SVG path string that draws a line between those
  // points but rounds each joint up to a given radius.
  roundedPath(coords, radius, offsetRect = {}) {
    const point = (x, y) => [
      Math.round(x) - (offsetRect.left || 0),
      Math.round(y) - (offsetRect.top || 0)
    ]
    // For each pair of points, add curve start/end points by moving along the line
    // from each endpoint up to the desired radius
    const interpolatedCoords = []
    for (let i = 0, len = coords.length; i < len - 1; i++) {
      const [x1, y1] = coords[i]
      const [x2, y2] = coords[i + 1]
      const dX = x2 - x1
      const dY = y2 - y1
      const dist = Math.sqrt(dX * dX + dY * dY)
      const fraction = Math.min(0.5, radius / dist)
      if (i === 0) {
        interpolatedCoords.push(point(x1, y1)) //start point
      } else {
        interpolatedCoords.push(point(x1 + (dX * fraction), y1 + (dY * fraction))) //previous curve end
      }
      if (i === len - 2) {
        interpolatedCoords.push(point(x2, y2)) //end point
      } else {
        interpolatedCoords.push(
          point(x2 - (dX * fraction), y2 - (dY * fraction)), //next curve start
          point(x2, y2) //next curve control point
        )
      }
    }
    coords = interpolatedCoords

    // Build the full SVG path string from the interpolated curve points
    const path = [`M${ coords[0][0] },${ coords[0][1] }`]
    for (let i = 1, len = coords.length; i < len; i += 3) {
      path.push(`L${ interpolatedCoords[i][0] },${ interpolatedCoords[i][1] }`)
      if (i < len - 1) {
        path.push(`Q${ interpolatedCoords[i + 1][0] },${ interpolatedCoords[i + 1][1] } ${ interpolatedCoords[i + 2][0] },${ interpolatedCoords[i + 2][1] }`)
      }
    }
    return path.join(' ')
  }
}

export default graphUtil
